import { useEffect, useRef, useState } from 'react';
import Flag from '../reusable/Flag/Flag';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

import { useI18n } from '../i18n/I18nProvider';
import { t } from '../i18n/i18n';

/**
 * The top bar's language control — N78. It used to drive Weglot; it now
 * switches the app's own translations (src/i18n, src/locales), so nothing is
 * sent to a third party and every language works on every domain.
 *
 * The list is `LANGUAGES` in src/i18n/i18n.js — each language in its own name,
 * its English name under it, and a flag (`public/flags/`, by country code).
 */
export default function LanguageSwitcher() {
  const { language: current, languages: list, changeLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const toggle = useRef(null);
  const options = useRef([]);

  const languages = list.map((l) => l.code);
  const LANGS = Object.fromEntries(list.map((l) => [l.code, l]));
  const nameOf = (code) => LANGS[code]?.native ?? String(code ?? '').toUpperCase();

  // Closes on a click anywhere else, as the top bar's other dropdowns do.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!box.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Opening puts the focus on the language in use, so the arrow keys start
  // from where the reader is.
  useEffect(() => {
    if (!open) return;
    const at = Math.max(0, languages.indexOf(current));
    options.current[at]?.focus();
  }, [open, languages, current]);

  if (languages.length < 2) return null;

  const close = ({ refocus = false } = {}) => {
    setOpen(false);
    if (refocus) toggle.current?.focus();
  };

  const choose = (code) => {
    close({ refocus: true });
    // The whole app re-mounts in the new language — this control included.
    if (code !== current) changeLanguage(code);
  };

  /** Arrow keys, Home/End and Escape over the open menu; Tab leaves it. */
  const onMenuKey = (e) => {
    const items = options.current.filter(Boolean);
    const at = items.indexOf(document.activeElement);
    const move = (i) => items[(i + items.length) % items.length]?.focus();
    if (e.key === 'ArrowDown') { e.preventDefault(); move(at + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(at - 1); }
    else if (e.key === 'Home') { e.preventDefault(); move(0); }
    else if (e.key === 'End') { e.preventDefault(); move(items.length - 1); }
    else if (e.key === 'Escape') { e.preventDefault(); close({ refocus: true }); }
    else if (e.key === 'Tab') close();
  };

  return (
    <div className={`lang-switcher${open ? ' is-open' : ''}`} ref={box}>
      <button
        type="button"
        ref={toggle}
        className="topbar__pill lang-switcher__toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('Language')}: ${nameOf(current)}`}
        title={`${t('Language')}: ${nameOf(current)}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="icon lang-switcher__globe" style={icon('topbar-globe')} aria-hidden="true" />
        <span className="lang-switcher__code">{String(current ?? '').toUpperCase()}</span>
        <span className="icon lang-switcher__caret" style={icon('chevron-down')} aria-hidden="true" />
      </button>

      {open && (
        <div className="lang-switcher__menu" onKeyDown={onMenuKey}>
          <span className="lang-switcher__heading" id="lang-switcher-heading">
            {t('Language')}
          </span>
          <ul className="lang-switcher__list" role="listbox" aria-labelledby="lang-switcher-heading">
            {languages.map((code, i) => {
              const lang = LANGS[code];
              const selected = code === current;
              return (
                <li key={code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    ref={(el) => { options.current[i] = el; }}
                    className={`lang-switcher__option${selected ? ' is-current' : ''}`}
                    onClick={() => choose(code)}
                  >
                    {lang?.flag ? (
                      <Flag code={lang.flag} className="lang-switcher__flag" />
                    ) : (
                      <span className="lang-switcher__flag lang-switcher__flag--blank" />
                    )}
                    <span className="lang-switcher__names">
                      <span className="lang-switcher__native">{nameOf(code)}</span>
                      {lang && lang.english !== lang.native && (
                        <span className="lang-switcher__english">{lang.english}</span>
                      )}
                    </span>
                    {selected && (
                      <span className="icon lang-switcher__check" style={icon('check-sm')} aria-hidden="true" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
